import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  ArrowLeft, Loader2, Save, GitBranch, MoreVertical,
  Download, Upload, Image as ImageIcon, FileText,
  ZoomIn, ZoomOut, Maximize2, PanelRightClose, PanelRightOpen,
  Search, Users, Undo2, Redo2, ShieldCheck, UploadCloud, CheckCircle2,
  Sparkles,
} from 'lucide-react'
import { loadWorkflow, saveWorkflow, generateWorkflow } from '@/api/designers'
import {
  approvalsValidate, approvalsPublish,
  type ApprovalValidationIssue,
} from '@/livepreview/api'
import { parseApiError } from '@/livepreview/utils'
import { useProjectStore } from '@/store/projectStore'
import { useThemeStore } from '@/store/themeStore'
import { useAgentRefresh } from '@/hooks/useAgentRefresh'
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard'
import { useBuilderContextStore } from '@/store/builderContextStore'
import DiscardChangesModal from '@/components/common/DiscardChangesModal'

import type { ContextMenuState, SelectedComponent } from './workflow/types'
import { useWorkflowState } from './workflow/useWorkflowState'
import { parseXpdl } from './workflow/xpdlParser'
import { generateXpdl } from './workflow/xpdlGenerator'
import { isActivity } from './workflow/geometry'
import { exportToPng, exportToPdf, downloadXpdl } from './workflow/svgExport'
import WorkflowCanvas from './workflow/WorkflowCanvas'
import Minimap from './workflow/Minimap'
import ContextMenu from './workflow/ContextMenu'
import PropertiesPanel from './workflow/PropertiesPanel'
import CanvasToolbar from './workflow/CanvasToolbar'
import HintBar from './workflow/HintBar'
import ParticipantModal from './workflow/ParticipantModal'
import SearchDialog from './workflow/SearchDialog'
import ApprovalBindingModal from './workflow/ApprovalBindingModal'
import ValidationResultModal from './workflow/ValidationResultModal'
import GenerateWorkflowModal from './workflow/GenerateWorkflowModal'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import { ReadOnlyBadge } from '@/auth/ReadOnlyBadge'
import { useRunOnlyRedirect } from '@/auth/useRunOnlyRedirect'

export default function WorkflowDesignerPage() {
  const { project, objName } = useParams<{ project: string; objName: string }>()
  useRunOnlyRedirect(project)
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const projectName = project || ''
  const workflowName = objName || ''
  // ?bind=<transaction>&triggers=add,edit — passed when arriving from a
  // transaction's "Design Workflow" / "Open in Workflow Designer", so we
  // auto-link the approval binding instead of making the user link again.
  const bindHint = searchParams.get('bind') || ''
  const triggersHint = (searchParams.get('triggers') || '')
    .split(',').map(s => s.trim().toLowerCase()).filter(Boolean)
  // ?action=<button> — this document is ONE action button's approval chain
  // (approval.actions[] on the bound transaction), not the object's CRUD
  // chain. Validate / publish must target that chain, or they would read the
  // levels of — and version — the wrong workflow.
  const actionHint = (searchParams.get('action') || '').trim()
  const { currentProject, selectProject } = useProjectStore()
  const { theme } = useThemeStore()

  // Restore currentProject in store from URL param on refresh
  useEffect(() => {
    if (project && currentProject?.application.folder_name !== project) {
      selectProject(project)
    }
  }, [project, currentProject, selectProject])

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const { canEditObjectsOrLoading: canEdit } = useProjectPermissions()
  const [menuOpen, setMenuOpen] = useState(false)
  const [panelOpen, setPanelOpen] = useState(true)
  const [panelWidth, setPanelWidth] = useState(300)
  const [participantModalOpen, setParticipantModalOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({ visible: false, x: 0, y: 0, activity: null })
  const [editingName, setEditingName] = useState(false)

  // ---- Approval binding / validate / publish ----
  const [bindingModalOpen, setBindingModalOpen] = useState(false)
  const [validating, setValidating] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [validationOpen, setValidationOpen] = useState(false)
  const [validationIssues, setValidationIssues] = useState<ApprovalValidationIssue[]>([])

  // ---- Generate with AI ----
  const [genOpen, setGenOpen] = useState(false)
  const [generating, setGenerating] = useState(false)

  const svgContainerRef = useRef<HTMLDivElement>(null)
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 })

  const state = useWorkflowState()

  // Reload the workflow when an AI agent run touches project files so the
  // open designer reflects the agent's edits without a hard browser refresh.
  // Deferred while dirty so in-progress edits are never clobbered.
  const agentRefresh = useAgentRefresh(projectName, state.isDirty)

  // In-app unsaved-changes guard (matches the other designers). Also installs
  // the beforeunload warning, so the old manual handler below is removed.
  const { guard, allowNextUnload, showDiscardModal, confirmDiscard, cancelDiscard } =
    useUnsavedChangesGuard(state.isDirty)

  // Publish the open workflow as the Builder/Ask-AI "open object" so the agent
  // resolves "this workflow" / "this screen" / "here" to it instead of guessing.
  useEffect(() => {
    if (!workflowName) return
    useBuilderContextStore.getState().setOpenObject({
      obj_name: workflowName,
      obj_type: 'W',
      title: state.objectName || workflowName,
    })
    return () => useBuilderContextStore.getState().setOpenObject(null)
  }, [workflowName, state.objectName])

  // ---- Load workflow ----
  useEffect(() => {
    const load = async () => {
      if (!projectName || !workflowName) return
      setLoading(true)
      try {
        const data = await loadWorkflow(projectName, workflowName)
        if (data.content) {
          const parsed = parseXpdl(data.content)
          // Auto-link: if the saved workflow isn't bound yet but we arrived
          // from a transaction (?bind=…), pre-fill the binding so validate /
          // publish / generate work without a second linking step.
          const binding = parsed.binding?.bindObject || !bindHint
            ? parsed.binding
            : {
                bindObject: bindHint,
                bindTriggers: triggersHint.length ? triggersHint : ['add', 'edit'],
                profile: 'advanced' as const,
              }
          state.loadState(parsed.workflow, parsed.participants, parsed.objectName || workflowName, parsed.toolsConfig, binding)
        } else {
          state.setObjectName(workflowName)
          if (bindHint) {
            state.setBindObject(bindHint)
            state.setBindTriggers(triggersHint.length ? triggersHint : ['add', 'edit'])
          }
        }
      } catch {
        toast.error('Failed to load workflow')
        state.setObjectName(workflowName)
      } finally {
        setLoading(false)
      }
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectName, workflowName, agentRefresh])

  // ---- Auto-center workflow after loading ----
  const hasCenteredRef = useRef(false)
  useEffect(() => {
    if (!loading && !hasCenteredRef.current && canvasSize.width > 0 && canvasSize.height > 0) {
      hasCenteredRef.current = true
      requestAnimationFrame(() => {
        state.zoomFit(canvasSize.width, canvasSize.height)
      })
    }
  }, [loading, canvasSize, state])

  // ---- Canvas size tracking ----
  useEffect(() => {
    const el = svgContainerRef.current
    if (!el) return
    const observer = new ResizeObserver(entries => {
      for (const e of entries) {
        setCanvasSize({ width: e.contentRect.width, height: e.contentRect.height })
      }
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // ---- Save ----
  // Returns true on success so the discard-modal "Save & Continue" path can
  // proceed with the pending navigation only when the save actually persisted.
  const handleSave = useCallback(async (): Promise<boolean> => {
    setSaving(true)
    try {
      const xpdl = generateXpdl(state.objectNameRef.current, state.workflowRef.current, state.participantsRef.current, state.bindingRef.current)
      await saveWorkflow(projectName, workflowName, xpdl)
      state.setIsDirty(false)
      toast.success('Workflow saved successfully')
      return true
    } catch {
      toast.error('Failed to save workflow')
      return false
    } finally {
      setSaving(false)
    }
  }, [projectName, workflowName, state])

  // Discard-modal "Save & Continue": persist, then run the queued navigation.
  const handleSaveAndContinue = useCallback(async () => {
    const ok = await handleSave()
    if (ok) confirmDiscard()
  }, [handleSave, confirmDiscard])

  // Guarded back navigation — prompt to save/discard when there are edits.
  const handleBack = useCallback(() => {
    const selectedNodeId = (location.state as { selectedNodeId?: string })?.selectedNodeId
    guard(() => {
      allowNextUnload()
      navigate('/workbench', { state: { selectedNodeId } })
    })
  }, [guard, allowNextUnload, location.state, navigate])

  // ---- Export / Import ----
  const handleExportXpdl = useCallback(() => {
    const xpdl = generateXpdl(state.objectNameRef.current, state.workflowRef.current, state.participantsRef.current, state.bindingRef.current)
    downloadXpdl(xpdl, state.objectNameRef.current || 'workflow')
    setMenuOpen(false)
  }, [state])

  const handleImportXpdl = useCallback(() => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.xpdl'
    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) return
      try {
        const text = await file.text()
        const parsed = parseXpdl(text)
        state.loadState(parsed.workflow, parsed.participants, parsed.objectName, parsed.toolsConfig, parsed.binding)
        toast.success('XPDL imported successfully')
      } catch {
        toast.error('Failed to import XPDL file')
      }
    }
    input.click()
    setMenuOpen(false)
  }, [state])

  // ---- Validate / Publish (approval workflow) ----
  const handleValidate = useCallback(async () => {
    const bindObject = state.bindingRef.current.bindObject
    if (!bindObject) {
      toast.error('Set the approval binding (bound object) first')
      setBindingModalOpen(true)
      return
    }
    setValidating(true)
    try {
      const xpdl = generateXpdl(state.objectNameRef.current, state.workflowRef.current, state.participantsRef.current, state.bindingRef.current)
      const res = await approvalsValidate(projectName, bindObject, {
        xpdl, ...(actionHint ? { action: actionHint } : {}),
      })
      setValidationIssues(res.issues || [])
      setValidationOpen(true)
    } catch {
      toast.error('Validation failed')
    } finally {
      setValidating(false)
    }
  }, [projectName, state, actionHint])

  const handlePublish = useCallback(async () => {
    const bindObject = state.bindingRef.current.bindObject
    if (!bindObject) {
      toast.error('Set the approval binding (bound object) first')
      setBindingModalOpen(true)
      return
    }
    const notes = window.prompt('Optional notes for this version (what changed?):') ?? undefined
    setPublishing(true)
    try {
      const xpdl = generateXpdl(state.objectNameRef.current, state.workflowRef.current, state.participantsRef.current, state.bindingRef.current)
      // Persist the workflow file first, then publish an immutable version.
      await saveWorkflow(projectName, workflowName, xpdl)
      state.setIsDirty(false)
      const res = await approvalsPublish(projectName, bindObject, {
        xpdl, notes, ...(actionHint ? { action: actionHint } : {}),
      })
      if (res.created) {
        toast.success(`Published version ${res.version}`)
      } else {
        toast.success(`No change — already at version ${res.version}`)
      }
    } catch (err) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(msg || 'Publish failed')
    } finally {
      setPublishing(false)
    }
  }, [projectName, workflowName, state, actionHint])

  // ---- Generate with AI (approval workflow) ----
  const handleOpenGenerate = useCallback(() => {
    if (!state.bindingRef.current.bindObject) {
      // Generation is always scoped to the bound transaction — make the user
      // bind one first, opening the binding dialog so they can.
      toast('Bind this workflow to a transaction first')
      setBindingModalOpen(true)
      return
    }
    setGenOpen(true)
  }, [state])

  const handleGenerate = useCallback(async (description: string) => {
    const bindObject = state.bindingRef.current.bindObject
    if (!bindObject) {
      toast('Bind this workflow to a transaction first')
      setBindingModalOpen(true)
      return
    }
    setGenerating(true)
    try {
      const res = await generateWorkflow({
        project: projectName,
        obj_name: workflowName,
        description,
        bound_object: bindObject,
        bind_triggers: state.bindingRef.current.bindTriggers ?? ['add', 'edit'],
      })
      const parsed = parseXpdl(res.content)
      state.loadState(
        parsed.workflow,
        parsed.participants,
        parsed.objectName || workflowName,
        parsed.toolsConfig,
        parsed.binding,
      )
      // loadState resets the dirty flag — mark dirty so the user is prompted to
      // Save the freshly generated workflow (and the beforeunload guard fires).
      state.setIsDirty(true)
      setGenOpen(false)
      toast.success(`Generated ${res.profile} workflow — review and Save when ready.`)
      if (res.summary.errors > 0) {
        setValidationIssues(
          (res.issues || []).map(i => ({
            severity: i.severity as ApprovalValidationIssue['severity'],
            code: i.code,
            message: i.message,
            where: i.where,
          })),
        )
        setValidationOpen(true)
      }
    } catch (err) {
      toast.error(parseApiError(err).message)
    } finally {
      setGenerating(false)
    }
  }, [projectName, workflowName, state])

  const handleExportImage = useCallback(() => {
    const svg = svgContainerRef.current?.querySelector('svg')
    if (svg) exportToPng(svg, state.objectNameRef.current || 'workflow')
    setMenuOpen(false)
  }, [state])

  const handleExportPdf = useCallback(() => {
    const svg = svgContainerRef.current?.querySelector('svg')
    if (svg) exportToPdf(svg, state.objectNameRef.current || 'workflow')
    setMenuOpen(false)
  }, [state])

  // ---- Selection + double-click ----
  const handleSelect = useCallback((component: SelectedComponent) => {
    state.selectComponent(component)
  }, [state])

  const handleDoubleClick = useCallback((component: SelectedComponent) => {
    state.selectComponent(component)
    if (!panelOpen) setPanelOpen(true)
  }, [state, panelOpen])

  // ---- Search select ----
  const handleSearchSelect = useCallback((type: 'activity' | 'transition', id: string) => {
    const wf = state.workflowRef.current
    if (type === 'activity') {
      const a = wf.activities.find(act => act.id === id)
      if (a) {
        state.selectComponent(a)
        state.setPanOffset({
          x: -(a.x + a.width / 2) * state.zoomLevel + canvasSize.width / 2,
          y: -(a.y + a.height / 2) * state.zoomLevel + canvasSize.height / 2,
        })
      }
    } else {
      const t = wf.transitions.find(tr => tr.id === id)
      if (t) {
        state.selectComponent(t)
        const from = wf.activities.find(a => a.id === t.from)
        const to = wf.activities.find(a => a.id === t.to)
        if (from && to) {
          const cx = (from.x + to.x) / 2
          const cy = (from.y + to.y) / 2
          state.setPanOffset({
            x: -cx * state.zoomLevel + canvasSize.width / 2,
            y: -cy * state.zoomLevel + canvasSize.height / 2,
          })
        }
      }
    }
  }, [state, canvasSize])

  // ---- Keyboard shortcuts ----
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) {
        return
      }

      const ctrl = e.metaKey || e.ctrlKey

      if (ctrl && e.key === 's') {
        e.preventDefault()
        handleSave()
      } else if (ctrl && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        state.undo()
      } else if (ctrl && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault()
        state.redo()
      } else if (ctrl && (e.key === 'Delete' || e.key === 'Backspace')) {
        e.preventDefault()
        const sel = state.getSelected()
        if (sel) state.deleteComponent(sel)
      } else if (ctrl && e.key === '=') {
        e.preventDefault()
        state.zoomIn()
      } else if (ctrl && e.key === '-') {
        e.preventDefault()
        state.zoomOut()
      } else if (ctrl && e.key === '0') {
        e.preventDefault()
        state.zoomFit(canvasSize.width, canvasSize.height)
      } else if (ctrl && e.key === 'c') {
        e.preventDefault()
        const sel = state.getSelected()
        if (sel && isActivity(sel) && sel.type !== 'start') {
          state.duplicateActivity(sel.id)
          toast.success('Activity duplicated')
        }
      } else if (ctrl && e.key === 'f') {
        e.preventDefault()
        setSearchOpen(true)
      } else if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        const sel = state.getSelected()
        if (sel && isActivity(sel)) {
          e.preventDefault()
          const amount = e.shiftKey ? 10 : 1
          let dx = 0, dy = 0
          if (e.key === 'ArrowLeft') dx = -amount
          if (e.key === 'ArrowRight') dx = amount
          if (e.key === 'ArrowUp') dy = -amount
          if (e.key === 'ArrowDown') dy = amount
          state.moveActivity(sel.id, sel.x + dx, sel.y + dy)
          state.saveState()
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleSave, state, canvasSize])

  // (Unsaved-changes beforeunload warning is installed by useUnsavedChangesGuard.)

  // ---- Close menu on outside click ----
  useEffect(() => {
    if (!menuOpen) return
    const close = () => setMenuOpen(false)
    window.addEventListener('click', close)
    return () => window.removeEventListener('click', close)
  }, [menuOpen])

  // ---- Panel resize ----
  const handlePanelResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const startX = e.pageX
    const startWidth = panelWidth

    const onMouseMove = (ev: MouseEvent) => {
      const delta = startX - ev.pageX
      setPanelWidth(Math.max(200, Math.min(500, startWidth + delta)))
    }
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }, [panelWidth])

  // ---- Hint message ----
  const hintMessage = state.transitionMode
    ? (state.transitionStartId
      ? 'Now select the activity to transition to.'
      : 'Select the activity from where you would like to create a transition.')
    : 'Drag from edge of an activity to another to connect them. Click empty space to pan.'

  // ---- Loading state ----
  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ flex: 1, minHeight: 0 }}>
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
          <span style={{ color: 'var(--color-text-secondary)' }}>Loading workflow...</span>
        </div>
      </div>
    )
  }

  const selected = state.getSelected()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* ---- Header ---- */}
      {/* Wraps (rather than clipping) so no control disappears when the toolbar
          is wider than the viewport — e.g. at 125%+ browser zoom. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', rowGap: 4,
        padding: '6px 0', borderBottom: '1px solid var(--color-border)', flexShrink: 0,
      }}>
        {/* Left: Back + workflow name */}
        <div className="flex items-center gap-2" style={{ minWidth: 0 }}>
          <button className="btn btn-ghost btn-sm" onClick={handleBack} title="Back">
            <ArrowLeft size={16} />
          </button>
          <GitBranch size={16} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
          {editingName ? (
            <input
              autoFocus
              value={state.objectName}
              onChange={e => state.setObjectName(e.target.value)}
              onBlur={() => setEditingName(false)}
              onKeyDown={e => { if (e.key === 'Enter') setEditingName(false) }}
              style={{
                fontSize: '0.9125rem', fontWeight: 600, padding: '2px 6px',
                background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
                borderRadius: 4, color: 'var(--color-text)', outline: 'none',
                minWidth: 120, maxWidth: 280,
              }}
            />
          ) : (
            <span
              onClick={() => setEditingName(true)}
              title="Click to rename"
              style={{
                fontSize: '0.9125rem', fontWeight: 600, cursor: 'text',
                color: 'var(--color-text)', padding: '2px 6px', borderRadius: 4,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-bg-secondary)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              {state.objectName || workflowName}
            </span>
          )}
          <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', flexShrink: 0 }}>
            {projectName}
          </span>
        </div>

        {/* Right: Actions — pushed right, wraps to its own row(s) when narrow. */}
        <div className="flex items-center gap-1" style={{ flexWrap: 'wrap', justifyContent: 'flex-end', marginLeft: 'auto', rowGap: 4 }}>
          {/* Generate with AI — draft the approval workflow from a description */}
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleOpenGenerate}
            disabled={generating}
            title="Generate this approval workflow from a description"
            style={{ gap: 4 }}
          >
            {generating ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles size={14} />}
            Generate with AI
          </button>

          {/* Approval binding chip — shows the bound object compactly */}
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setBindingModalOpen(true)}
            title="Approval binding"
            style={{ gap: 4, maxWidth: 220 }}
          >
            <ShieldCheck size={15} style={{ color: state.binding.bindObject ? 'var(--color-accent)' : 'var(--color-text-secondary)', flexShrink: 0 }} />
            <span style={{
              fontSize: '0.8125rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              color: state.binding.bindObject ? 'var(--color-text)' : 'var(--color-text-secondary)',
            }}>
              {state.binding.bindObject
                ? `${state.binding.bindObject}${state.binding.bindTriggers?.length ? ` · ${state.binding.bindTriggers.join('/')}` : ''}`
                : 'Bind approval'}
            </span>
          </button>

          <button
            className="btn btn-ghost btn-sm"
            onClick={handleValidate}
            disabled={validating}
            title="Validate this workflow against the approval engine"
            style={{ gap: 4 }}
          >
            {validating ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <CheckCircle2 size={14} />}
            Validate
          </button>

          <button
            className="btn btn-ghost btn-sm"
            onClick={handlePublish}
            disabled={publishing}
            title="Publish an immutable version of this approval workflow"
            style={{ gap: 4 }}
          >
            {publishing ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <UploadCloud size={14} />}
            Publish
          </button>

          <div style={{ width: 1, height: 18, background: 'var(--color-border)', margin: '0 2px' }} />

          <button className="btn btn-ghost btn-sm" onClick={() => setSearchOpen(true)} title="Search (Ctrl+F)">
            <Search size={15} />
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => setParticipantModalOpen(true)} title="Manage Participants">
            <Users size={15} />
          </button>

          <div style={{ width: 1, height: 18, background: 'var(--color-border)', margin: '0 2px' }} />

          <button className="btn btn-ghost btn-sm" onClick={state.undo} disabled={state.undoCount === 0} title="Undo (Ctrl+Z)">
            <Undo2 size={15} style={{ opacity: state.undoCount === 0 ? 0.3 : 1 }} />
          </button>
          <button className="btn btn-ghost btn-sm" onClick={state.redo} disabled={state.redoCount === 0} title="Redo (Ctrl+Y)">
            <Redo2 size={15} style={{ opacity: state.redoCount === 0 ? 0.3 : 1 }} />
          </button>

          <div style={{ width: 1, height: 18, background: 'var(--color-border)', margin: '0 2px' }} />

          {canEdit ? (
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving} style={{ gap: 4 }}>
              {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />}
              {saving ? 'Saving...' : 'Save'}
            </button>
          ) : (
            <ReadOnlyBadge />
          )}

          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setPanelOpen(!panelOpen)}
            title={panelOpen ? 'Hide Properties' : 'Show Properties'}
          >
            {panelOpen ? <PanelRightClose size={15} /> : <PanelRightOpen size={15} />}
          </button>

          {/* Export menu */}
          <div style={{ position: 'relative' }}>
            <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); setMenuOpen(!menuOpen) }} title="More options">
              <MoreVertical size={15} />
            </button>
            {menuOpen && (
              <div style={{
                position: 'absolute', right: 0, top: '100%', marginTop: 4,
                background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
                borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                minWidth: 180, zIndex: 100, overflow: 'hidden',
              }}>
                <MenuBtn icon={<Upload size={14} />} label="Import XPDL" onClick={handleImportXpdl} />
                <MenuBtn icon={<Download size={14} />} label="Export XPDL" onClick={handleExportXpdl} />
                <div style={{ height: 1, background: 'var(--color-border)' }} />
                <MenuBtn icon={<ImageIcon size={14} />} label="Export as Image" onClick={handleExportImage} />
                <MenuBtn icon={<FileText size={14} />} label="Export as PDF" onClick={handleExportPdf} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ---- Main Content ---- */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Canvas area */}
        <div ref={svgContainerRef} style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          <WorkflowCanvas
            workflow={state.workflow}
            participants={state.participants}
            selectedId={state.selectedId}
            selectedType={state.selectedType}
            transitionMode={state.transitionMode}
            transitionStartId={state.transitionStartId}
            zoomLevel={state.zoomLevel}
            panOffset={state.panOffset}
            theme={theme}
            onSelect={handleSelect}
            onMoveActivity={state.moveActivity}
            onSaveState={state.saveState}
            onZoomToward={state.zoomToward}
            onPanChange={state.setPanOffset}
            onTransitionModeClick={state.handleTransitionModeClick}
            onCancelTransitionMode={state.cancelTransitionMode}
            onDoubleClick={handleDoubleClick}
            onContextMenu={setContextMenu}
            onEdgeDragTransition={state.addTransition}
          />

          {/* Floating canvas toolbar */}
          <CanvasToolbar
            onAddActivity={() => state.addActivity()}
            onAddEnd={state.addEndActivity}
            onToggleTransition={state.toggleTransitionMode}
            transitionMode={state.transitionMode}
            theme={theme}
          />

          {/* Minimap */}
          <Minimap
            workflow={state.workflow}
            zoomLevel={state.zoomLevel}
            panOffset={state.panOffset}
            canvasWidth={canvasSize.width}
            canvasHeight={canvasSize.height}
            onPanChange={state.setPanOffset}
            theme={theme}
          />

          {/* Zoom controls */}
          <div style={{
            position: 'absolute', bottom: 12, right: 12,
            display: 'flex', alignItems: 'center', gap: 4,
            background: theme === 'light' ? 'rgba(255,255,255,0.9)' : 'rgba(30,30,30,0.9)',
            border: '1px solid var(--color-border)',
            borderRadius: 6,
            padding: '4px 6px',
          }}>
            <button className="btn btn-ghost btn-sm" onClick={state.zoomIn} title="Zoom In (Ctrl++)">
              <ZoomIn size={14} />
            </button>
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', minWidth: 36, textAlign: 'center' }}>
              {Math.round(state.zoomLevel * 100)}%
            </span>
            <button className="btn btn-ghost btn-sm" onClick={state.zoomOut} title="Zoom Out (Ctrl+-)">
              <ZoomOut size={14} />
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => state.zoomFit(canvasSize.width, canvasSize.height)} title="Fit to View (Ctrl+0)">
              <Maximize2 size={14} />
            </button>
          </div>

          {/* Search dialog */}
          <SearchDialog
            open={searchOpen}
            onClose={() => setSearchOpen(false)}
            workflow={state.workflow}
            onSelect={handleSearchSelect}
          />

          {/* Context menu */}
          <ContextMenu
            state={contextMenu}
            onClose={() => setContextMenu({ visible: false, x: 0, y: 0, activity: null })}
            onDelete={() => {
              const sel = state.getSelected()
              if (sel) state.deleteComponent(sel)
            }}
            onDuplicate={state.duplicateActivity}
            onAlign={state.alignActivity}
            onAddActivity={() => state.addActivity()}
            onAddEnd={state.addEndActivity}
          />
        </div>

        {/* Right panel: Properties only */}
        {panelOpen && (
          <>
            {/* Resize handle */}
            <div
              onMouseDown={handlePanelResize}
              style={{
                width: 6, cursor: 'col-resize', flexShrink: 0,
                position: 'relative', background: 'transparent',
              }}
              onMouseEnter={e => {
                const bar = e.currentTarget.querySelector('div') as HTMLElement
                if (bar) bar.style.background = 'var(--color-accent)'
              }}
              onMouseLeave={e => {
                const bar = e.currentTarget.querySelector('div') as HTMLElement
                if (bar) bar.style.background = 'var(--color-border)'
              }}
            >
              <div style={{
                position: 'absolute', left: 2, top: 0, bottom: 0, width: 1,
                background: 'var(--color-border)', transition: 'background 0.2s',
              }} />
            </div>

            <div style={{
              width: panelWidth, minWidth: 200, flexShrink: 0,
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
              background: 'var(--color-bg)',
              borderLeft: '1px solid var(--color-border)',
            }}>
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <PropertiesPanel
                  selected={selected}
                  workflow={state.workflow}
                  participants={state.participants}
                  getDistinctToolTypes={state.getDistinctToolTypes}
                  getToolsByType={state.getToolsByType}
                  getToolDefinition={state.getToolDefinition}
                  onUpdateActivity={state.updateActivity}
                  onUpdateTransition={state.updateTransition}
                  onDelete={state.deleteComponent}
                  onSaveState={state.saveState}
                  onManageParticipants={() => setParticipantModalOpen(true)}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* ---- Hint bar ---- */}
      <HintBar message={hintMessage} />

      {/* ---- Participant modal ---- */}
      <ParticipantModal
        open={participantModalOpen}
        onClose={() => setParticipantModalOpen(false)}
        participants={state.participants}
        onAdd={state.addParticipant}
        onUpdate={state.updateParticipant}
        onDelete={state.deleteParticipant}
        project={projectName}
      />

      {/* ---- Approval binding modal ---- */}
      <ApprovalBindingModal
        open={bindingModalOpen}
        onClose={() => setBindingModalOpen(false)}
        project={projectName}
        binding={state.binding}
        onSetBindObject={state.setBindObject}
        onSetBindTriggers={state.setBindTriggers}
      />

      {/* ---- Validation result modal ---- */}
      <ValidationResultModal
        open={validationOpen}
        onClose={() => setValidationOpen(false)}
        issues={validationIssues}
      />

      {/* ---- Generate with AI modal ---- */}
      <GenerateWorkflowModal
        open={genOpen}
        boundObject={state.binding.bindObject || ''}
        triggers={state.binding.bindTriggers ?? []}
        onClose={() => setGenOpen(false)}
        onGenerate={handleGenerate}
        loading={generating}
      />

      {/* ---- Unsaved-changes guard ---- */}
      <DiscardChangesModal
        open={showDiscardModal}
        onCancel={cancelDiscard}
        onDiscard={confirmDiscard}
        onSave={handleSaveAndContinue}
        saving={saving}
      />
    </div>
  )
}

// ---- Helpers ----

function MenuBtn({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        width: '100%', padding: '8px 12px', fontSize: '0.8625rem',
        color: 'var(--color-text)', background: 'transparent',
        border: 'none', cursor: 'pointer', textAlign: 'left',
      }}
      onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-bg)')}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
    >
      {icon}
      {label}
    </button>
  )
}
